import { useEffect, useState } from "react";
import { AlertTriangle, Lock, ShieldCheck } from "lucide-react";
import { api, apiError, formatDateTime } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ROLE_LABELS, useAuth } from "@/context/AuthContext";
import {
  AdminBanner,
  AdminButton,
  AdminCard,
  AdminField,
  AdminPageHead,
  AdminSpinner,
  AdminTable,
  EmptyState,
  adminInputClass,
} from "@/components/admin/AdminUi";

/* ------------------------------------------------------ Donation Settings */

export function DonationSettings() {
  const BKA_PHONE = "+1 242-436-1317";
  const BKA_EMAIL = "info@bahamaskidneyassociation.org";

  const [form, setForm] = useState(null);
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const [saving, setSaving] = useState(false);
  const [secret, setSecret] = useState("");

  useEffect(() => {
    Promise.all([api.get("/admin/donations/fygaro"), api.get("/admin/settings")])
      .then(([fygaroRes, settingsRes]) => {
        const f = fygaroRes.data || {};
        const data = settingsRes.data || {};
        const currentPhone = (data["site.phone"] || "").trim();
        const currentEmail = (data["site.email"] || "").trim();
        setForm({
          enabled: Boolean(f.enabled),
          button_url: f.button_url || "",
          api_key: f.api_key || "",
          currency: f.currency || "BSD",
          secret_configured: Boolean(f.secret_configured),
          phone: !currentPhone || currentPhone === "(242) 123-4567" ? BKA_PHONE : currentPhone,
          email: !currentEmail || currentEmail === "info@bahamaskidney.org" ? BKA_EMAIL : currentEmail,
        });
      })
      .catch((e) => setBanner({ status: "error", message: apiError(e) }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const fygaroPayload = {
        enabled: Boolean(form.enabled),
        button_url: form.button_url,
        api_key: form.api_key,
        currency: form.currency,
      };
      if (secret.trim()) fygaroPayload.secret_key = secret.trim();

      const [fygaroRes, settingsRes] = await Promise.all([
        api.put("/admin/donations/fygaro", fygaroPayload),
        api.put("/admin/settings", {
          values: { "site.phone": form.phone, "site.email": form.email },
        }),
      ]);
      const f = fygaroRes.data || {};
      const data = settingsRes.data || {};
      setForm((current) => ({
        ...current,
        enabled: Boolean(f.enabled),
        button_url: f.button_url || "",
        api_key: f.api_key || "",
        currency: f.currency || "BSD",
        secret_configured: Boolean(f.secret_configured),
        phone: data["site.phone"] || BKA_PHONE,
        email: data["site.email"] || BKA_EMAIL,
      }));
      setSecret("");
      setBanner({
        status: "ok",
        message: f.enabled
          ? "Saved. Secure Fygaro JWT donations are enabled."
          : "Saved. Fygaro settings are stored; donations remain disabled until you switch them on.",
      });
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
    setSaving(false);
  };

  if (form === null) return <AdminSpinner />;
  const ready = Boolean(form.button_url.trim() && form.api_key.trim() && form.secret_configured);

  return (
    <div data-testid="cms-donations">
      <Seo title="Donation Settings | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead
        title="Donation Settings"
        subtitle="Connect BKA's Fygaro Pro account and manage the public donation details."
      >
        <AdminButton variant="emerald" testId="cms-donations-save" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </AdminButton>
      </AdminPageHead>

      <AdminBanner status={banner.status} message={banner.message} onDismiss={() => setBanner({ status: "idle", message: "" })} />

      {!ready && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5" role="status">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-bold text-amber-900">Fygaro setup is not complete yet</p>
            <p className="mt-1 text-sm leading-relaxed text-amber-800">
              To activate secure JWT donations, add the Fygaro Payment Button URL, API/Public Key and Secret Key. The Secret Key is never shown again after saving.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]">
        <AdminCard className="space-y-5 p-6">
          <h2 className="font-display text-lg font-bold text-bka-navy">Fygaro Pro Integration</h2>

          <AdminField label="Fygaro Payment Button / Link URL" htmlFor="fygaro-url" hint="Use BKA's Variable Amount / Payment Button URL. Must start with https://.">
            <input id="fygaro-url" value={form.button_url} onChange={(e) => setForm((f) => ({ ...f, button_url: e.target.value }))} className={adminInputClass} placeholder="https://www.fygaro.com/en/pb/…" />
          </AdminField>

          <AdminField label="Fygaro API / Public Key" htmlFor="fygaro-api-key" hint="Paste the Public/API Key generated under Fygaro Settings → API Credentials.">
            <input id="fygaro-api-key" value={form.api_key} onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))} className={adminInputClass} autoComplete="off" />
          </AdminField>

          <AdminField
            label={form.secret_configured ? "Replace Fygaro Secret Key" : "Fygaro Secret Key"}
            htmlFor="fygaro-secret"
            hint={form.secret_configured ? "A secret is already stored securely. Leave blank to keep it unchanged." : "Paste the Secret Key once. It is stored server-side and is never returned to the browser."}
          >
            <input id="fygaro-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} className={adminInputClass} autoComplete="new-password" placeholder={form.secret_configured ? "•••••••••••••••• (already configured)" : "Paste secret key"} />
          </AdminField>

          <AdminField label="Currency" htmlFor="fygaro-currency">
            <select id="fygaro-currency" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} className={adminInputClass}>
              <option value="BSD">BSD — Bahamian Dollar</option>
              <option value="USD">USD — US Dollar</option>
            </select>
          </AdminField>

          <label className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <span>
              <span className="block text-sm font-bold text-bka-navy">Enable Online Donations</span>
              <span className="mt-1 block text-xs text-slate-500">Only enable after the URL, API key and secret are configured.</span>
            </span>
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} className="h-5 w-5 accent-emerald-600" />
          </label>

          <div className="border-t border-slate-100 pt-5">
            <h2 className="font-display text-lg font-bold text-bka-navy">Public Contact Details</h2>
          </div>
          <AdminField label="Phone Number" htmlFor="donation-phone">
            <input id="donation-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={adminInputClass} placeholder={BKA_PHONE} />
          </AdminField>
          <AdminField label="Email Address" htmlFor="donation-email">
            <input id="donation-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={adminInputClass} placeholder={BKA_EMAIL} />
          </AdminField>
        </AdminCard>

        <AdminCard className="p-6">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-bka-emerald"><ShieldCheck className="h-5 w-5" /></span>
          <h2 className="mt-4 font-display text-base font-bold text-bka-navy">Secure server-side flow</h2>
          <ol className="mt-3 space-y-3 text-sm text-slate-600">
            <li><strong className="text-bka-navy">1.</strong> Donor chooses amount and purpose on BKA's website.</li>
            <li><strong className="text-bka-navy">2.</strong> FastAPI signs the amount server-side using BKA's Fygaro Secret Key.</li>
            <li><strong className="text-bka-navy">3.</strong> Fygaro receives the signed JWT and handles the card/payment details.</li>
          </ol>
          <div className="mt-5 rounded-2xl bg-slate-50 p-4 text-xs leading-5 text-slate-600">
            The Secret Key is never placed in the React bundle and is never returned by the CMS API after saving.
          </div>
        </AdminCard>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- Site Settings */

const FIELDS = [
  { key: "site.name", label: "Site Name" },
  { key: "site.tagline", label: "Tagline" },
  { key: "site.phone", label: "Public Phone Number" },
  { key: "site.email", label: "Public Email Address" },
  { key: "site.address", label: "Address" },
  { key: "social.facebook_url", label: "Facebook URL" },
  { key: "social.instagram_url", label: "Instagram URL" },
  { key: "social.youtube_url", label: "YouTube URL" },
  { key: "social.linkedin_url", label: "LinkedIn URL" },
];

export function SiteSettings() {
  const [settings, setSettings] = useState(null);
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBanner, setPwBanner] = useState({ status: "idle", message: "" });
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then((r) => setSettings(r.data))
      .catch((e) => setBanner({ status: "error", message: apiError(e) }));
    api.get("/admin/dashboard").then((r) => setEmailStatus(r.data.email || null)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    const values = {};
    [...FIELDS.map((f) => f.key), "site.medical_disclaimer", "forms.notification_email", "forms.notifications_enabled"].forEach(
      (k) => {
        values[k] = settings[k];
      }
    );
    try {
      const { data } = await api.put("/admin/settings", { values });
      setSettings(data);
      setBanner({ status: "ok", message: "Settings saved." });
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
    setSaving(false);
  };

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) {
      setPwBanner({ status: "error", message: "The new passwords do not match." });
      return;
    }
    try {
      await api.post("/admin/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      setPwBanner({ status: "ok", message: "Your password has been updated." });
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      setPwBanner({ status: "error", message: apiError(e) });
    }
  };

  if (settings === null) return <AdminSpinner />;
  const set = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div data-testid="cms-settings">
      <Seo title="Settings | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead title="Settings" subtitle="Site details, notifications and your account.">
        <AdminButton variant="emerald" testId="cms-settings-save" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </AdminButton>
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <AdminCard className="space-y-5 p-6">
          <h2 className="font-display text-base font-bold text-bka-navy">Site Details</h2>
          <p className="text-xs text-slate-500">
            These values appear across the public website. Nothing is hard-coded.
          </p>
          {FIELDS.map((f) => (
            <AdminField key={f.key} label={f.label} htmlFor={`set-${f.key}`}>
              <input
                id={`set-${f.key}`}
                data-testid={`cms-settings-${f.key.replace(/\./g, "-")}`}
                value={settings[f.key] || ""}
                onChange={set(f.key)}
                className={adminInputClass}
              />
            </AdminField>
          ))}
        </AdminCard>

        <div className="space-y-5">
          <AdminCard className="space-y-5 p-6">
            <h2 className="font-display text-base font-bold text-bka-navy">Form Notifications</h2>
            {emailStatus && !emailStatus.enabled && (
              <div
                data-testid="cms-settings-email-disabled"
                className="rounded-xl border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-bka-ink"
              >
                <strong className="block text-bka-navy">Email Notifications Not Configured</strong>
                {emailStatus.notice}
              </div>
            )}
            {emailStatus && emailStatus.enabled && (
              <div
                data-testid="cms-settings-email-sender"
                className="rounded-xl border-l-4 border-emerald-600 bg-emerald-50 p-4 text-sm text-bka-ink"
              >
                <strong className="block text-bka-navy">Emails are sent as</strong>
                {emailStatus.from_name} &lt;{emailStatus.from_email}&gt; · replies go to{" "}
                {emailStatus.reply_to}
              </div>
            )}
            <label htmlFor="notif-enabled" className="flex cursor-pointer items-start gap-3 text-sm text-bka-ink">
              <input
                id="notif-enabled"
                data-testid="cms-settings-notifications-enabled"
                type="checkbox"
                checked={settings["forms.notifications_enabled"] !== false}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, "forms.notifications_enabled": e.target.checked }))
                }
                className="mt-0.5 h-4 w-4 accent-[color:var(--bka-emerald)]"
              />
              <span>
                <strong className="block text-bka-navy">Email me new submissions</strong>
                Submissions are always saved in the CMS, whether or not email is enabled.
              </span>
            </label>
            <AdminField
              label="Notification Recipients"
              htmlFor="notif-email"
              hint="Separate multiple addresses with commas."
            >
              <input
                id="notif-email"
                data-testid="cms-settings-notification-email"
                value={settings["forms.notification_email"] || ""}
                onChange={set("forms.notification_email")}
                className={adminInputClass}
                placeholder="staff@example.org, second@example.org"
              />
            </AdminField>
          </AdminCard>

          <AdminCard className="space-y-5 p-6">
            <h2 className="font-display text-base font-bold text-bka-navy">Medical Disclaimer</h2>
            <AdminField label="Site-wide Disclaimer" htmlFor="set-disclaimer">
              <textarea
                id="set-disclaimer"
                data-testid="cms-settings-disclaimer"
                rows={6}
                value={settings["site.medical_disclaimer"] || ""}
                onChange={set("site.medical_disclaimer")}
                className={adminInputClass}
              />
            </AdminField>
          </AdminCard>

          <AdminCard className="space-y-5 p-6">
            <h2 className="font-display text-base font-bold text-bka-navy">Change Your Password</h2>
            <AdminBanner
              status={pwBanner.status}
              message={pwBanner.message}
              onDismiss={() => setPwBanner({ status: "idle", message: "" })}
              testId="cms-password-banner"
            />
            <AdminField label="Current Password" htmlFor="pw-current" required>
              <input id="pw-current" data-testid="cms-password-current" type="password" autoComplete="current-password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} className={adminInputClass} />
            </AdminField>
            <AdminField label="New Password" htmlFor="pw-new" required hint="At least 10 characters.">
              <input id="pw-new" data-testid="cms-password-new" type="password" autoComplete="new-password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} className={adminInputClass} />
            </AdminField>
            <AdminField label="Confirm New Password" htmlFor="pw-confirm" required>
              <input id="pw-confirm" data-testid="cms-password-confirm" type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} className={adminInputClass} />
            </AdminField>
            <AdminButton
              testId="cms-password-submit"
              onClick={changePassword}
              disabled={!pw.current_password || !pw.new_password}
            >
              Update Password
            </AdminButton>
          </AdminCard>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Users */

const BLANK_USER = { name: "", email: "", password: "", role: "editor" };

export function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState(BLANK_USER);
  const [banner, setBanner] = useState({ status: "idle", message: "" });

  const load = () =>
    api
      .get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch((e) => {
        setUsers([]);
        setBanner({ status: "error", message: apiError(e) });
      });

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    const cleanName = form.name.trim();
    const cleanEmail = form.email.trim().toLowerCase();
    const existing = (users || []).find((u) => (u.email || "").trim().toLowerCase() === cleanEmail);

    if (existing) {
      setBanner({
        status: "error",
        message: `${existing.name} already has a CMS account (${existing.email}) and is currently ${existing.is_active ? "active" : "inactive"}. Do not add the same email twice.`,
      });
      return;
    }

    try {
      await api.post("/admin/users", { ...form, name: cleanName, email: cleanEmail });
      const loginUrl = `${window.location.origin}${process.env.PUBLIC_URL || ""}/admin/login`;
      setBanner({
        status: "ok",
        message: `${cleanName} was added successfully. CMS login: ${loginUrl}. Provide the temporary password to the user securely.`,
      });
      setForm(BLANK_USER);
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const update = async (id, body, successMessage) => {
    try {
      await api.put(`/admin/users/${id}`, body);
      setBanner({ status: "ok", message: successMessage });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}'s access?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setBanner({ status: "ok", message: `${u.name} was removed.` });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  if (users === null) return <AdminSpinner />;

  return (
    <div data-testid="cms-users">
      <Seo title="Users & Roles | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead title="Users & Roles" subtitle="Least-privilege access for the association's team." />

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr]">
        <AdminCard className="overflow-hidden">
          {users.length === 0 ? (
            <EmptyState message="No users yet." testId="cms-users-empty" />
          ) : (
            <AdminTable columns={["Name", "Role", "Last Sign In", "Status", "Actions"]} testId="cms-users-table">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50/70" data-testid={`cms-user-row-${u.id}`}>
                  <td className="px-5 py-4">
                    <p className="font-bold text-bka-navy">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email}</p>
                  </td>
                  <td className="px-5 py-4">
                    <label htmlFor={`role-${u.id}`} className="sr-only">
                      Role for {u.name}
                    </label>
                    <select
                      id={`role-${u.id}`}
                      data-testid={`cms-user-role-${u.id}`}
                      value={u.role}
                      disabled={u.role === "super_admin" && me.role !== "super_admin"}
                      onChange={(e) => update(u.id, { role: e.target.value }, `${u.name}'s role updated.`)}
                      className={`${adminInputClass} w-40 py-1.5 text-xs`}
                    >
                      {Object.entries(ROLE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-4 text-slate-500">
                    {u.last_login_at ? formatDateTime(u.last_login_at) : "Never"}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-bold ${
                        u.is_active
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-slate-100 text-slate-500"
                      }`}
                    >
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      <AdminButton
                        variant="ghost"
                        testId={`cms-user-toggle-${u.id}`}
                        onClick={() =>
                          update(
                            u.id,
                            { is_active: !u.is_active },
                            `${u.name} was ${u.is_active ? "deactivated" : "reactivated"}.`
                          )
                        }
                        className="px-3 py-1.5 text-xs"
                      >
                        {u.is_active ? "Deactivate" : "Activate"}
                      </AdminButton>
                      {u.role !== "super_admin" && u.id !== me.id && (
                        <AdminButton
                          variant="danger"
                          testId={`cms-user-delete-${u.id}`}
                          onClick={() => remove(u)}
                          className="px-3 py-1.5 text-xs"
                        >
                          Remove
                        </AdminButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </AdminTable>
          )}
        </AdminCard>

        <AdminCard className="space-y-5 p-6">
          <h2 className="font-display text-base font-bold text-bka-navy">Add a Team Member</h2>
          <AdminField label="Full Name" htmlFor="nu-name" required>
            <input id="nu-name" data-testid="cms-user-new-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={adminInputClass} />
          </AdminField>
          <AdminField label="Email Address" htmlFor="nu-email" required>
            <input id="nu-email" data-testid="cms-user-new-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={adminInputClass} />
          </AdminField>
          <AdminField label="Temporary Password" htmlFor="nu-password" required hint="At least 10 characters. They can change it after signing in.">
            <input id="nu-password" data-testid="cms-user-new-password" type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={adminInputClass} />
          </AdminField>
          <AdminField label="Role" htmlFor="nu-role">
            <select id="nu-role" data-testid="cms-user-new-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className={adminInputClass}>
              {Object.entries(ROLE_LABELS)
                .filter(([v]) => v !== "super_admin" || me.role === "super_admin")
                .map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
            </select>
          </AdminField>
          <p className="rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
            Creating a user gives them immediate CMS access. The same email cannot be added twice.
            After creation, share the CMS login address and temporary password with that person securely.
          </p>
          <AdminButton
            variant="emerald"
            testId="cms-user-create"
            onClick={create}
            disabled={!form.name || !form.email || form.password.length < 10}
          >
            Add User
          </AdminButton>

          <div className="border-t border-slate-100 pt-5 text-xs leading-relaxed text-slate-500">
            <p className="mb-2 font-bold text-bka-navy">Role permissions</p>
            <ul className="space-y-1.5">
              <li><strong>Super Admin</strong> — full access including users and settings.</li>
              <li><strong>Administrator</strong> — content, moderation, settings and users.</li>
              <li><strong>Editor</strong> — content, media and moderation.</li>
              <li><strong>Reviewer</strong> — nominations, testimonials and enquiries only.</li>
            </ul>
          </div>
        </AdminCard>
      </div>
    </div>
  );
}

/* --------------------------------------------------------- Audit Log */

export function AuditLog() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .get("/admin/audit-log", { params: { action: filter || undefined } })
        .then((r) => setItems(r.data))
        .catch(() => setItems([]));
    }, filter ? 350 : 0);
    return () => clearTimeout(t);
  }, [filter]);

  return (
    <div data-testid="cms-audit-log">
      <Seo title="Audit Log | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead title="Audit Log" subtitle="A record of sign-ins and sensitive administrative changes.">
        <label htmlFor="audit-filter" className="sr-only">
          Filter by action
        </label>
        <input
          id="audit-filter"
          data-testid="cms-audit-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by action, e.g. nomination"
          className={`${adminInputClass} w-64`}
        />
      </AdminPageHead>

      <AdminCard className="overflow-hidden">
        {items === null ? (
          <AdminSpinner />
        ) : items.length === 0 ? (
          <EmptyState message="No audit entries match this filter." testId="cms-audit-empty" />
        ) : (
          <AdminTable columns={["When", "User", "Action", "Entity", "Details"]} testId="cms-audit-table">
            {items.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50/70" data-testid={`cms-audit-row-${a.id}`}>
                <td className="whitespace-nowrap px-5 py-4 text-slate-500">{formatDateTime(a.created_at)}</td>
                <td className="px-5 py-4 text-slate-600">{a.user_email || "—"}</td>
                <td className="px-5 py-4 font-bold text-bka-navy">{a.action}</td>
                <td className="px-5 py-4 text-slate-500">{a.entity_type || "—"}</td>
                <td className="max-w-xs px-5 py-4 text-xs text-slate-500">
                  {Object.entries(a.metadata || {})
                    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
                    .join(" · ") || "—"}
                </td>
              </tr>
            ))}
          </AdminTable>
        )}
      </AdminCard>
    </div>
  );
}
